import React from 'react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { Resource } from '../../../types';
import { FlashcardSetConfigurationForm } from './BasicResourceConfigurationForm';

interface QuizConfigurationFormProps {
  resource: Resource;
  config?: Record<string, any>;
  onUpdate: (updates: Partial<Resource>) => void;
}

export const QuizConfigurationForm: React.FC<QuizConfigurationFormProps> = ({
  resource,
  config,
  onUpdate
}) => {
  const { t } = useTranslation();
  const updateResource = (field: string, value: any) => {
    onUpdate({ [field]: value });
  };

  const updateQuestions = (questions: any[]) => {
    onUpdate({ questions });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('quizConfiguration.numberOfItems')}</label>
        <input
          type="number"
          value={config?.items === undefined ? (resource.items || 5) : config.items}
          onChange={(e) => updateResource('items', parseInt(e.target.value))}
          className="input-field"
          min="1"
          max="20"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('quizConfiguration.shuffleQuestions')}</label>
        <input
          type="checkbox"
          checked={config?.shuffle === undefined ? (resource.shuffle !== false) : config.shuffle}
          onChange={(e) => updateResource('shuffle', e.target.checked)}
          className="rounded border-gray-300"
        />
        <span className="ml-2 text-sm text-gray-600">{t('quizConfiguration.randomizeQuestionOrder')}</span>
      </div>

      {/* Quiz Questions Configuration */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <h4 className="text-sm font-medium text-gray-700">{t('quizConfiguration.quizQuestions')}</h4>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => {
                // Bulk add sample questions
                const sampleQuestions = [
                  {
                    id: `q${Date.now()}-1`,
                    prompt: t('quizConfiguration.howManyObjectsDoYouSee'),
                    answer: 8,
                    choices: [6, 7, 8, 9, 10]
                  },
                  {
                    id: `q${Date.now()}-2`,
                    prompt: t('quizConfiguration.whatNumberComesAfter9'),
                    answer: 10,
                    choices: [8, 9, 10, 11, 12]
                  },
                  {
                    id: `q${Date.now()}-3`,
                    prompt: t('quizConfiguration.whatIs5Plus3'),
                    answer: 8,
                    choices: [6, 7, 8, 9, 10]
                  }
                ];
                const newQuestions = [...(resource.questions || []), ...sampleQuestions];
                updateQuestions(newQuestions);
              }}
              className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            >
              📝 {t('quizConfiguration.addSampleQuestions')}
            </button>
            <button
              type="button"
              onClick={() => {
                // Clear all questions
                if (window.confirm(t('quizConfiguration.confirmRemoveAllQuestions'))) {
                  updateQuestions([]);
                }
              }}
              className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
            >
              🗑️ {t('quizConfiguration.clearAll')}
            </button>
          </div>
        </div>
        
        {(config?.questions || resource.questions || []).map((question: any, index: number) => (
          <div key={question.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <h5 className="text-sm font-medium text-gray-800">{t('quizConfiguration.question')} {index + 1}</h5>
                <div className="flex space-x-1">
                  <button
                    type="button"
                    onClick={() => {
                      if (index > 0) {
                        const newQuestions = [...(config?.questions || resource.questions || [])];
                        [newQuestions[index], newQuestions[index - 1]] = [newQuestions[index - 1], newQuestions[index]];
                        updateQuestions(newQuestions);
                      }
                    }}
                    disabled={index === 0}
                    className="p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t('quizConfiguration.moveUp')}
                  >
                    ⬆️
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (index < (resource.questions || []).length - 1) {
                        const newQuestions = [...(config?.questions || resource.questions || [])];
                        [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
                        updateQuestions(newQuestions);
                      }
                    }}
                    disabled={index === (resource.questions || []).length - 1}
                    className="p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t('quizConfiguration.moveDown')}
                  >
                    ⬇️
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  const newQuestions = (config?.questions || resource.questions || []).filter((_: any, i: number) => i !== index);
                  updateQuestions(newQuestions);
                }}
                className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                title={t('quizConfiguration.deleteQuestion')}
              >
                🗑️
              </button>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.questionPrompt')}</label>
              <input
                type="text"
                value={question.prompt || ''}
                onChange={(e) => {
                  const newQuestions = [...(config?.questions || resource.questions || [])];
                  newQuestions[index] = { ...question, prompt: e.target.value };
                  updateQuestions(newQuestions);
                }}
                className="input-field text-sm"
                placeholder={t('quizConfiguration.enterQuestionPrompt')}
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.correctAnswer')}</label>
              <input
                type="text"
                value={question.answer || 0}
                onChange={(e) => {
                  const newQuestions = [...(config?.questions || resource.questions || [])];
                  newQuestions[index] = { ...question, answer: e.target.value };
                  updateQuestions(newQuestions);
                }}
                className="input-field text-sm"
                placeholder={t('quizConfiguration.enterCorrectAnswer')}
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.answerChoices')}</label>
              <input
                type="text"
                value={(question.choices || []).join(', ')}
                onChange={(e) => {
                  const choices = e.target.value.split(',').map(c => c.trim());
                  const newQuestions = [...(config?.questions || resource.questions || [])];
                  newQuestions[index] = { ...question, choices };
                  updateQuestions(newQuestions);
                }}
                className="input-field text-sm"
                placeholder={t('quizConfiguration.choicesPlaceholder')}
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-500">
                  {question.choices?.length || 0} {t('quizConfiguration.choices')}
                </span>
                <div className="flex space-x-1">
                  <button
                    type="button"
                    onClick={() => {
                      const newChoices = [...(question.choices || []), (question.choices?.length || 0) + 1];
                      const newQuestions = [...(config?.questions || resource.questions || [])];
                      newQuestions[index] = { ...question, choices: newChoices };
                      updateQuestions(newQuestions);
                    }}
                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                  >
                    ➕ {t('quizConfiguration.addChoice')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const newChoices = (question.choices || []).slice(0, -1);
                      const newQuestions = [...(config?.questions || resource.questions || [])];
                      newQuestions[index] = { ...question, choices: newChoices };
                      updateQuestions(newQuestions);
                    }}
                    disabled={(question.choices || []).length <= 2}
                    className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ➖ {t('quizConfiguration.removeChoice')}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 text-xs text-gray-600">
              <span>{t('quizConfiguration.correctAnswer')}: {question.answer}</span>
              {question.choices && !question.choices.includes(question.answer) && (
                <span className="text-red-600">⚠️ {t('quizConfiguration.answerNotInChoices')}</span>
              )}
            </div>
            
            {/* Resource Component Configuration */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-gray-600">{t('quizConfiguration.interactiveResource')}</label>
                <button
                  type="button"
                  onClick={() => {
                    const newQuestions = [...(config?.questions || resource.questions || [])];
                    if (newQuestions[index].resource_component) {
                      delete newQuestions[index].resource_component;
                    } else {
                      newQuestions[index].resource_component = {
                        type: 'count_objects_canvas',
                        max_objects: 8,
                        randomize: true,
                        touch_feedback: 'ping'
                      };
                    }
                    updateQuestions(newQuestions);
                  }}
                  className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                >
                  {question.resource_component ? t('quizConfiguration.removeResource') : t('quizConfiguration.addResource')}
                </button>
              </div>
              
              {question.resource_component && (
                <div className="space-y-2 bg-blue-50 p-3 rounded border border-blue-200">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.resourceType')}</label>
                    <select
                      value={question.resource_component.type || 'count_objects_canvas'}
                      onChange={(e) => {
                        const newQuestions = [...(config?.questions || resource.questions || [])];
                        newQuestions[index].resource_component = {
                          ...newQuestions[index].resource_component,
                          type: e.target.value
                        };
                        updateQuestions(newQuestions);
                      }}
                      className="input-field text-sm"
                    >
                      <option value="count_objects_canvas">{t('quizConfiguration.countObjects')}</option>
                      <option value="ten_frame">{t('quizConfiguration.tenFrame')}</option>
                      <option value="number_line">{t('quizConfiguration.numberLine')}</option>
                      <option value="flashcard_set">{t('quizConfiguration.flashcards')}</option>
                      <option value="grid_activity">{t('quizConfiguration.gridActivity')}</option>
                    </select>
                  </div>
                  
                  {question.resource_component.type === 'count_objects_canvas' && (
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.objectsCount')}</label>
                        <input
                          type="number"
                          value={question.resource_component.objects || question.resource_component.max_objects || 8}
                          onChange={(e) => {
                            const newQuestions = [...(config?.questions || resource.questions || [])];
                            newQuestions[index].resource_component = {
                              ...newQuestions[index].resource_component,
                              objects: parseInt(e.target.value) || 8,
                              max_objects: parseInt(e.target.value) || 8
                            };
                            updateQuestions(newQuestions);
                          }}
                          className="input-field text-sm"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.layout')}</label>
                        <select
                          value={question.resource_component.layout || 'random_clustered'}
                          onChange={(e) => {
                            const newQuestions = [...(config?.questions || resource.questions || [])];
                            newQuestions[index].resource_component = {
                              ...newQuestions[index].resource_component,
                              layout: e.target.value
                            };
                            updateQuestions(newQuestions);
                          }}
                          className="input-field text-sm"
                        >
                          <option value="random_clustered">{t('quizConfiguration.randomClustered')}</option>
                          <option value="two_groups">{t('quizConfiguration.twoGroups')}</option>
                          <option value="linear">{t('quizConfiguration.linear')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.randomize')}</label>
                        <input
                          type="checkbox"
                          checked={question.resource_component.randomize !== false}
                          onChange={(e) => {
                            const newQuestions = [...(config?.questions || resource.questions || [])];
                            newQuestions[index].resource_component = {
                              ...newQuestions[index].resource_component,
                              randomize: e.target.checked
                            };
                            updateQuestions(newQuestions);
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="ml-2 text-xs text-gray-600">{t('quizConfiguration.randomizeObjectPositions')}</span>
                      </div>
                    </div>
                  )}

                  {question.resource_component.type === 'ten_frame' && (
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.frameValue')}</label>
                        <input
                          type="number"
                          value={question.resource_component.frame_value || question.resource_component.ten_frame || 12}
                          onChange={(e) => {
                            const newQuestions = [...(config?.questions || resource.questions || [])];
                            newQuestions[index].resource_component = {
                              ...newQuestions[index].resource_component,
                              frame_value: parseInt(e.target.value) || 12,
                              ten_frame: parseInt(e.target.value) || 12
                            };
                            updateQuestions(newQuestions);
                          }}
                          className="input-field text-sm"
                          min="0"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.rows')}</label>
                          <input
                            type="number"
                            value={question.resource_component.rows || 2}
                            onChange={(e) => {
                              const newQuestions = [...(config?.questions || resource.questions || [])];
                              newQuestions[index].resource_component = {
                                ...newQuestions[index].resource_component,
                                rows: parseInt(e.target.value) || 2
                              };
                              updateQuestions(newQuestions);
                            }}
                            className="input-field text-sm"
                            min="1"
                            max="4"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.columns')}</label>
                          <input
                            type="number"
                            value={question.resource_component.cols || 5}
                            onChange={(e) => {
                              const newQuestions = [...(config?.questions || resource.questions || [])];
                              newQuestions[index].resource_component = {
                                ...newQuestions[index].resource_component,
                                cols: parseInt(e.target.value) || 5
                              };
                              updateQuestions(newQuestions);
                            }}
                            className="input-field text-sm"
                            min="1"
                            max="10"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {question.resource_component.type === 'number_line' && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.minValue')}</label>
                          <input
                            type="number"
                            value={question.resource_component.min || 0}
                            onChange={(e) => {
                              const newQuestions = [...(config?.questions || resource.questions || [])];
                              newQuestions[index].resource_component = {
                                ...newQuestions[index].resource_component,
                                min: parseInt(e.target.value) || 0
                              };
                              updateQuestions(newQuestions);
                            }}
                            className="input-field text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.maxValue')}</label>
                          <input
                            type="number"
                            value={question.resource_component.max || 20}
                            onChange={(e) => {
                              const newQuestions = [...(config?.questions || resource.questions || [])];
                              newQuestions[index].resource_component = {
                                ...newQuestions[index].resource_component,
                                max: parseInt(e.target.value) || 20
                              };
                              updateQuestions(newQuestions);
                            }}
                            className="input-field text-sm"
                            min="1"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.tickEvery')}</label>
                          <input
                            type="number"
                            value={question.resource_component.tick_every || 1}
                            onChange={(e) => {
                              const newQuestions = [...(config?.questions || resource.questions || [])];
                              newQuestions[index].resource_component = {
                                ...newQuestions[index].resource_component,
                                tick_every: parseInt(e.target.value) || 1
                              };
                              updateQuestions(newQuestions);
                            }}
                            className="input-field text-sm"
                            min="1"
                            max="10"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.markPosition')}</label>
                          <input
                            type="number"
                            value={question.resource_component.mark_position || question.resource_component.mark || 15}
                            onChange={(e) => {
                              const newQuestions = [...(config?.questions || resource.questions || [])];
                              newQuestions[index].resource_component = {
                                ...newQuestions[index].resource_component,
                                mark_position: parseInt(e.target.value),
                                mark: parseInt(e.target.value)
                              };
                              updateQuestions(newQuestions);
                            }}
                            className="input-field text-sm"
                            min="0"
                            max="100"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {question.resource_component.type === 'grid_activity' && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.rows')}</label>
                          <input
                            type="number"
                            value={question.resource_component.rows || 4}
                            onChange={(e) => {
                              const newQuestions = [...(config?.questions || resource.questions || [])];
                              newQuestions[index].resource_component = {
                                ...newQuestions[index].resource_component,
                                rows: parseInt(e.target.value) || 4
                              };
                              updateQuestions(newQuestions);
                            }}
                            className="input-field text-sm"
                            min="1"
                            max="10"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.columns')}</label>
                          <input
                            type="number"
                            value={question.resource_component.cols || 6}
                            onChange={(e) => {
                              const newQuestions = [...(config?.questions || resource.questions || [])];
                              newQuestions[index].resource_component = {
                                ...newQuestions[index].resource_component,
                                cols: parseInt(e.target.value) || 6
                              };
                              updateQuestions(newQuestions);
                            }}
                            className="input-field text-sm"
                            min="1"
                            max="10"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.bank')}</label>
                        <input
                          type="text"
                          value={(question.resource_component.bank || [0, 1, 2, 3, 4, 5]).join(', ')}
                          onChange={(e) => {
                            const bank = e.target.value.split(',').map(item => parseInt(item.trim())).filter(n => !isNaN(n));
                            const newQuestions = [...(config?.questions || resource.questions || [])];
                            newQuestions[index].resource_component = {
                              ...newQuestions[index].resource_component,
                              bank: bank
                            };
                            updateQuestions(newQuestions);
                          }}
                          className="input-field text-sm"
                          placeholder={t('quizConfiguration.bankPlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.mode')}</label>
                        <select
                          value={question.resource_component.mode || 'read_and_select'}
                          onChange={(e) => {
                            const newQuestions = [...(config?.questions || resource.questions || [])];
                            newQuestions[index].resource_component = {
                              ...newQuestions[index].resource_component,
                              mode: e.target.value
                            };
                            updateQuestions(newQuestions);
                          }}
                          className="input-field text-sm"
                        >
                          <option value="read_and_select">{t('quizConfiguration.readAndSelect')}</option>
                          <option value="drag_and_drop">{t('quizConfiguration.dragAndDrop')}</option>
                          <option value="click_to_fill">{t('quizConfiguration.clickToFill')}</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {question.resource_component.type === 'flashcard_set' && (
                    <div className="space-y-2">
                      <FlashcardSetConfigurationForm 
                        resource={resource} 
                        onUpdate={(updates: any) => {
                          const newQuestions = [...(config?.questions || resource.questions || [])];
                          newQuestions[index].resource_component = {
                            ...newQuestions[index].resource_component,
                            ...updates
                          };
                          updateQuestions(newQuestions);
                        }} 
                        config={config?.questions?.[index]?.resource_component} 
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        
        {/* Add Question Form */}
        <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
          <h5 className="text-sm font-medium text-gray-800">{t('quizConfiguration.addNewQuestion')}</h5>
          
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.questionPrompt')}</label>
            <input
              type="text"
              id={`new-prompt-${resource.id}`}
              className="input-field text-sm"
              placeholder={t('quizConfiguration.enterQuestionPrompt')}
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.correctAnswer')}</label>
            <input
              type="text"
              id={`new-answer-${resource.id}`}
              className="input-field text-sm"
              placeholder={t('quizConfiguration.enterCorrectAnswer')}
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.choices')}</label>
            <input
              type="text"
              id={`new-choices-${resource.id}`}
              className="input-field text-sm"
              placeholder={t('quizConfiguration.enterChoices')}
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('quizConfiguration.includeInteractiveResource')}</label>
            <input
              type="checkbox"
              id={`new-resource-${resource.id}`}
              className="rounded border-gray-300"
            />
            <span className="ml-2 text-xs text-gray-600">{t('quizConfiguration.addInteractiveComponent')}</span>
          </div>
          
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => {
                const promptInput = document.getElementById(`new-prompt-${resource.id}`) as HTMLInputElement;
                const answerInput = document.getElementById(`new-answer-${resource.id}`) as HTMLInputElement;
                const choicesInput = document.getElementById(`new-choices-${resource.id}`) as HTMLInputElement;
                
                const prompt = promptInput?.value.trim();
                const answer = answerInput?.value;
                const choicesText = choicesInput?.value.trim();
                const includeResource = (document.getElementById(`new-resource-${resource.id}`) as HTMLInputElement)?.checked;
                
                if (!prompt) {
                  alert(t('quizConfiguration.enterQuestionPrompt'));
                  return;
                }
                
                const choices = (choicesText || '').split(',').map(c => c.trim());
                
                if (choices.length === 0) {
                  alert(t('quizConfiguration.enterChoices'));
                  return;
                }
                
                if (!choices.includes(answer)) {
                  alert(t('quizConfiguration.correctAnswerMustBeOneOfTheChoices'));
                  return;
                }
                
                const newQuestion: any = {
                  id: `q${Date.now()}`,
                  prompt,
                  answer,
                  choices
                };

                // Add resource component if requested
                if (includeResource) {
                  newQuestion.resource_component = {
                    type: 'count_objects_canvas',
                    max_objects: answer,
                    randomize: true,
                    touch_feedback: 'ping'
                  };
                }
                
                const newQuestions = [...(config?.questions || resource.questions || []), newQuestion];
                updateQuestions(newQuestions);
                
                // Clear form
                if (promptInput) promptInput.value = '';
                if (answerInput) answerInput.value = '';
                if (choicesInput) choicesInput.value = '';
                const resourceCheckbox = document.getElementById(`new-resource-${resource.id}`) as HTMLInputElement;
                if (resourceCheckbox) resourceCheckbox.checked = false;
              }}
              className="btn-primary flex-1 text-sm py-2"
            >
              ➕ {t('quizConfiguration.addQuestion')}
            </button>
            
            <button
              type="button"
              onClick={() => {
                // Quick add with default values
                const newQuestion = {
                  id: `q${Date.now()}`,
                  prompt: `Question ${(config?.questions || resource.questions || []).length + 1}`,
                  answer: 1,
                  choices: [1, 2, 3, 4, 5]
                };
                const newQuestions = [...(config?.questions || resource.questions || []), newQuestion];
                updateQuestions(newQuestions);
              }}
              className="btn-secondary flex-1 text-sm py-2"
            >
              {t('quizConfiguration.quickAdd')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
